// src/pages/CRM/SFA/QuoteDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  Tooltip,
  Tab,
  Tabs,
  Container,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  ShoppingCart as ProductIcon,
  LocalShipping as ShippingIcon,
  Gavel as TermsIcon,
  TrendingUp as OpportunityIcon
} from '@mui/icons-material';
import { format, isPast } from 'date-fns';
import { useSnackbar } from 'notistack';

import { quoteService } from './sfaService';
import {
  QuoteStatus,
  QuoteStatusLabels,
  QuoteStatusColors,
  BillingFrequencies,
  formatCurrency,
  isQuoteEditable,
  canApproveQuote
} from './sfaEnums';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from "../../../../utils/axiosInstance";

// --- Custom Tab Panel ---
const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ py: 3, animation: 'fadeIn 0.3s ease-in' }}>
    {value === index && children}
  </Box>
);

// --- Stat Card Component ---
const StatCard = ({ icon, label, value, color, subValue }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }
    }}
  >
    <Avatar variant="rounded" sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, my: 0.5 }}>
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {subValue}
        </Typography>
      )}
    </Box>
  </Paper>
);

// --- Info Row Component ---
const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={500}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500} color="text.primary">
      {value || '-'}
    </Typography>
  </Box>
);

const QuoteDetail = () => {
  const { chatbotId, quoteId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Permissions
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const checkPerms = async () => {
      const editPerm = await hasPermission('crm', 'edit');
      setCanEdit(editPerm);
    };
    checkPerms();
  }, []);

  useEffect(() => {
    loadQuote();
  }, [quoteId]);

  const loadQuote = async () => {
    setLoading(true);
    try {
      const response = await quoteService.getById(quoteId);
      setQuote(response.data || response.quote);
    } catch (error) {
      console.error('Error loading quote:', error);
      enqueueSnackbar('Failed to load quote', { variant: 'error' });
      navigate(`/chatbot/${chatbotId}/crm/quotes`);
    } finally {
      setLoading(false);
    }
  };

  // Status change handlers
  const handleStatusChange = async (newStatus) => {
    try {
      await quoteService.updateStatus(quoteId, { status: newStatus });
      enqueueSnackbar(`Quote marked as ${QuoteStatusLabels[newStatus]}`, { variant: 'success' });
      loadQuote();
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('Failed to update quote status', { variant: 'error' });
    }
    setMenuAnchor(null);
  };

  // Sync to opportunity
  const handleSyncToOpportunity = async () => {
    try {
      await quoteService.syncToOpportunity(quoteId);
      enqueueSnackbar('Quote synced to opportunity', { variant: 'success' });
      loadQuote();
    } catch (error) {
      console.error('Error syncing:', error);
      enqueueSnackbar('Failed to sync quote to opportunity', { variant: 'error' });
    }
    setMenuAnchor(null);
  };

  // Approve quote
  const handleApproveQuote = async () => {
    setApprovalLoading(true);
    try {
      await quoteService.approve(quoteId, { approval_notes: approvalNotes });
      enqueueSnackbar('Quote approved successfully', { variant: 'success' });
      setApprovalDialogOpen(false);
      setApprovalNotes('');
      loadQuote();
    } catch (error) {
      console.error('Error approving quote:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to approve quote', { variant: 'error' });
    } finally {
      setApprovalLoading(false);
    }
    setMenuAnchor(null);
  };

  // Reject quote
  const handleRejectQuote = async () => {
    if (!rejectionReason.trim()) {
      enqueueSnackbar('Please provide a reason for rejection', { variant: 'warning' });
      return;
    }
    setApprovalLoading(true);
    try {
      await quoteService.reject(quoteId, { rejection_reason: rejectionReason });
      enqueueSnackbar('Quote rejected', { variant: 'info' });
      setRejectionDialogOpen(false);
      setRejectionReason('');
      loadQuote();
    } catch (error) {
      console.error('Error rejecting quote:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to reject quote', { variant: 'error' });
    } finally {
      setApprovalLoading(false);
    }
    setMenuAnchor(null);
  };

  // Generate PDF
  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await axiosInstance.get(`/api/crm/quotes/${quoteId}/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Check if response is actually a PDF
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Error response returned as JSON
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.message || 'Failed to generate PDF');
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quote.quote_number || 'quote'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('PDF generated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      enqueueSnackbar(error.message || 'Failed to generate PDF', { variant: 'error' });
    } finally {
      setGeneratingPdf(false);
    }
    setMenuAnchor(null);
  };

  // Calculate totals
  const calculateTotals = useCallback(() => {
    if (!quote) return { subtotal: 0, discount: 0, tax: 0, shipping: 0, total: 0 };

    const lineItems = quote.line_items || [];
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = item.discount_amount || (itemSubtotal * (item.discount_percent || 0) / 100);
      return sum + (itemSubtotal - itemDiscount);
    }, 0);

    const discountAmt = quote.discount_amount || (subtotal * (quote.discount_percent || 0) / 100);
    const afterDiscount = subtotal - discountAmt;
    const taxAmt = afterDiscount * (quote.tax_percent || 0) / 100;
    const total = afterDiscount + taxAmt + (quote.shipping_cost || 0);

    return {
      subtotal,
      discount: discountAmt,
      tax: taxAmt,
      shipping: quote.shipping_cost || 0,
      total
    };
  }, [quote]);

  const totals = calculateTotals();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">Quote not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes`)}
          sx={{ mt: 2 }}
        >
          Back to Quotes
        </Button>
      </Box>
    );
  }

  const isExpired = quote.valid_until && isPast(new Date(quote.valid_until));
  const editable = isQuoteEditable(quote.status);

  return (
    <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', pb: 5, mt: { xs: 2, sm: 10 } }}>
      {/* --- Top Header Area --- */}
      <Paper sx={{ px: { xs: 1.5, sm: 3 }, py: 3, mb: 4, borderRadius: 0, borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
        <Container maxWidth="xl">
          {/* Breadcrumb / Back */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes`)}
              sx={{ color: 'text.secondary', mr: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Back to Quotes
            </Button>
          </Box>

          {/* Title Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {quote.name}
                </Typography>
                <Chip
                  label={quote.quote_number || 'No ID'}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1, fontWeight: 600, bgcolor: 'grey.50', borderColor: 'grey.300' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={QuoteStatusLabels[quote.status]}
                  color={QuoteStatusColors[quote.status]}
                  size="small"
                />
                {isExpired && quote.status !== QuoteStatus.ACCEPTED && (
                  <Chip label="Expired" color="error" size="small" variant="outlined" />
                )}
                {quote.account && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {quote.account.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={generatingPdf ? <CircularProgress size={20} /> : <PdfIcon />}
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
              >
                Download PDF
              </Button>
              {canEdit && editable && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/${quoteId}/edit`)}
                  sx={{ borderRadius: 2, px: 3, boxShadow: theme.shadows[2] }}
                >
                  Edit Quote
                </Button>
              )}
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Status Banner for Accepted/Rejected */}
        {quote.status === QuoteStatus.ACCEPTED && (
          <Alert
            severity="success"
            variant="filled"
            icon={<CheckIcon fontSize="inherit" />}
            sx={{ mb: 4, borderRadius: 2, fontWeight: 500, boxShadow: theme.shadows[2] }}
          >
            <Typography variant="subtitle1" fontWeight={700} component="span">
              Quote Accepted!
            </Typography>
            {' — '}
            {quote.accepted_at ? `Accepted on ${format(new Date(quote.accepted_at), 'MMMM d, yyyy')}` : 'Successfully won this deal.'}
          </Alert>
        )}

        {quote.status === QuoteStatus.REJECTED && (
          <Alert
            severity="error"
            variant="filled"
            icon={<CloseIcon fontSize="inherit" />}
            sx={{ mb: 4, borderRadius: 2, fontWeight: 500, boxShadow: theme.shadows[2] }}
          >
            <Typography variant="subtitle1" fontWeight={700} component="span">
              Quote Rejected
            </Typography>
            {quote.rejection_reason && ` — Reason: ${quote.rejection_reason}`}
          </Alert>
        )}

        {/* Pending Approval Banner */}
        {quote.status === QuoteStatus.PENDING_APPROVAL && (
          <Alert
            severity="warning"
            variant="filled"
            sx={{ mb: 4, borderRadius: 2, fontWeight: 500, boxShadow: theme.shadows[2] }}
            action={
              canEdit && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => setApprovalDialogOpen(true)}
                    sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => setRejectionDialogOpen(true)}
                    sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    Reject
                  </Button>
                </Box>
              )
            }
          >
            <Typography variant="subtitle1" fontWeight={700} component="span">
              Approval Required
            </Typography>
            {' — '}
            {quote.total_discount_percent > 0
              ? `Discount of ${quote.total_discount_percent.toFixed(1)}% exceeds approval threshold.`
              : 'This quote requires approval before it can be sent.'}
          </Alert>
        )}

        {/* Approved Banner (shows approval info) */}
        {quote.status === QuoteStatus.APPROVED && quote.approved_at && (
          <Alert
            severity="info"
            icon={<CheckIcon fontSize="inherit" />}
            sx={{ mb: 4, borderRadius: 2, fontWeight: 500 }}
          >
            <Typography variant="subtitle1" fontWeight={700} component="span">
              Quote Approved
            </Typography>
            {' — '}
            {`Approved on ${format(new Date(quote.approved_at), 'MMMM d, yyyy')}`}
            {quote.approved_by_name && ` by ${quote.approved_by_name}`}
            {quote.approval_notes && `. Notes: ${quote.approval_notes}`}
          </Alert>
        )}

        {/* --- Key Metrics Grid --- */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<MoneyIcon />}
              color="#2e7d32"
              label="Total Amount"
              value={formatCurrency(totals.total, quote.currency)}
              subValue={`${quote.line_items?.length || 0} line items`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<ReceiptIcon />}
              color="#1976d2"
              label="Subtotal"
              value={formatCurrency(totals.subtotal, quote.currency)}
              subValue={totals.discount > 0 ? `Discount: ${formatCurrency(totals.discount, quote.currency)}` : 'No discounts applied'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<CalendarIcon />}
              color={isExpired ? '#d32f2f' : '#ff9800'}
              label="Valid Until"
              value={quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : 'Not set'}
              subValue={isExpired ? 'Expired' : 'Active'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<PersonIcon />}
              color="#9c27b0"
              label="Contact"
              value={quote.contact ? (quote.contact.full_name || `${quote.contact.first_name || ''} ${quote.contact.last_name || ''}`.trim()) : 'Not assigned'}
              subValue={quote.contact?.email || ''}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* --- LEFT COLUMN: Tabs & Main Content --- */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: theme.shadows[1] }}>
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': { fontWeight: 600, minHeight: 64, textTransform: 'none', fontSize: '0.95rem' },
                  '& .Mui-selected': { color: 'primary.main', bgcolor: 'white' }
                }}
              >
                <Tab icon={<DescriptionIcon />} iconPosition="start" label="Details" />
                <Tab icon={<ProductIcon />} iconPosition="start" label={`Line Items (${quote.line_items?.length || 0})`} />
                <Tab icon={<ShippingIcon />} iconPosition="start" label="Addresses" />
                <Tab icon={<TermsIcon />} iconPosition="start" label="Terms" />
              </Tabs>

              {/* DETAILS TAB */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ px: 4 }}>
                  <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 3 }}>Quote Information</Typography>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <InfoRow label="Quote Name" value={quote.name} />
                      <InfoRow label="Quote Number" value={quote.quote_number} />
                      <InfoRow label="Status" value={QuoteStatusLabels[quote.status]} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoRow label="Valid Until" value={quote.valid_until ? format(new Date(quote.valid_until), 'MMMM d, yyyy') : 'Not set'} />
                      <InfoRow 
                        label="Billing Frequency" 
                        value={BillingFrequencies.find(f => f.value === quote.billing_frequency)?.label || quote.billing_frequency || 'One-Time'} 
                      />
                      <InfoRow label="Payment Terms" value={quote.payment_terms} />
                    </Grid>
                    {quote.description && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={500}>Description</Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{quote.description}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </TabPanel>

              {/* LINE ITEMS TAB */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ px: 3 }}>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 700 }}>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Discount</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(quote.line_items || []).length > 0 ? (
                          (quote.line_items || []).map((item, index) => {
                            const itemSubtotal = item.quantity * item.unit_price;
                            const itemDiscount = item.discount_amount || (itemSubtotal * (item.discount_percent || 0) / 100);
                            const itemTotal = itemSubtotal - itemDiscount;

                            return (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {item.product?.name || item.name || 'Product'}
                                    </Typography>
                                    {item.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {item.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(item.unit_price, quote.currency)}
                                </TableCell>
                                <TableCell align="right">
                                  {item.discount_percent > 0 && (
                                    <Chip label={`${item.discount_percent}%`} size="small" color="warning" />
                                  )}
                                  {item.discount_amount > 0 && !item.discount_percent && (
                                    <Typography variant="body2">
                                      {formatCurrency(item.discount_amount, quote.currency)}
                                    </Typography>
                                  )}
                                  {!item.discount_percent && !item.discount_amount && '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={600}>
                                    {formatCurrency(itemTotal, quote.currency)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                              <ProductIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                              <Typography color="text.secondary">No line items in this quote.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {(quote.line_items || []).length > 0 && (
                          <>
                            <TableRow>
                              <TableCell colSpan={4} align="right">
                                <Typography variant="body2">Subtotal</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">{formatCurrency(totals.subtotal, quote.currency)}</Typography>
                              </TableCell>
                            </TableRow>
                            {totals.discount > 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="body2" color="error">Discount</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="error">-{formatCurrency(totals.discount, quote.currency)}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {totals.tax > 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="body2">Tax ({quote.tax_percent}%)</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">{formatCurrency(totals.tax, quote.currency)}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {totals.shipping > 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="body2">Shipping</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">{formatCurrency(totals.shipping, quote.currency)}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell colSpan={4} align="right">
                                <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" color="primary" fontWeight={700}>{formatCurrency(totals.total, quote.currency)}</Typography>
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </TabPanel>

              {/* ADDRESSES TAB */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ px: 4 }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom fontWeight={700} color="primary">Billing Address</Typography>
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        {quote.billing_address ? (
                          <>
                            <Typography variant="body1" fontWeight={600}>{quote.billing_address.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{quote.billing_address.street}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {quote.billing_address.city}, {quote.billing_address.state} {quote.billing_address.postal_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{quote.billing_address.country}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No billing address provided</Typography>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom fontWeight={700} color="primary">Shipping Address</Typography>
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        {quote.shipping_address ? (
                          <>
                            <Typography variant="body1" fontWeight={600}>{quote.shipping_address.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{quote.shipping_address.street}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {quote.shipping_address.city}, {quote.shipping_address.state} {quote.shipping_address.postal_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{quote.shipping_address.country}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No shipping address provided</Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>

              {/* TERMS TAB */}
              <TabPanel value={tabValue} index={3}>
                <Box sx={{ px: 4 }}>
                  {quote.terms_and_conditions && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom fontWeight={700}>Terms and Conditions</Typography>
                      <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {quote.terms_and_conditions}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {quote.notes && (
                    <Box>
                      <Typography variant="h6" gutterBottom fontWeight={700}>Internal Notes</Typography>
                      <Paper variant="outlined" sx={{ p: 3, bgcolor: 'warning.50', borderRadius: 2, borderColor: 'warning.200' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {quote.notes}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {!quote.terms_and_conditions && !quote.notes && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <TermsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">No terms or notes added to this quote.</Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            </Paper>
          </Grid>

          {/* --- RIGHT COLUMN: Sidebar --- */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* Summary Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>QUOTE SUMMARY</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                      <Typography variant="body2">{formatCurrency(totals.subtotal, quote.currency)}</Typography>
                    </Box>
                    {totals.discount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Discount</Typography>
                        <Typography variant="body2" color="error">-{formatCurrency(totals.discount, quote.currency)}</Typography>
                      </Box>
                    )}
                    {totals.tax > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Tax</Typography>
                        <Typography variant="body2">{formatCurrency(totals.tax, quote.currency)}</Typography>
                      </Box>
                    )}
                    {totals.shipping > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Shipping</Typography>
                        <Typography variant="body2">{formatCurrency(totals.shipping, quote.currency)}</Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" fontWeight={700}>Total</Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">{formatCurrency(totals.total, quote.currency)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Account Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>ACCOUNT</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {quote.account ? (
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', p: 1.5, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients/${quote.account.id}`)}
                    >
                      <Avatar variant="rounded" sx={{ bgcolor: 'primary.main' }}><BusinessIcon /></Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{quote.account.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{quote.account.industry || 'No Industry'}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No account linked</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Contact Widget */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>CONTACT</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {quote.contact ? (
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', p: 1.5, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${quote.contact.id}`)}
                    >
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {(quote.contact.full_name || quote.contact.first_name || '?')[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {quote.contact.full_name || `${quote.contact.first_name || ''} ${quote.contact.last_name || ''}`.trim() || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {quote.contact.email}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No contact linked</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Opportunity Widget */}
              {quote.opportunity && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>OPPORTUNITY</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', p: 1.5, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/${quote.opportunity.id}`)}
                    >
                      <Avatar variant="rounded" sx={{ bgcolor: 'success.main' }}><OpportunityIcon /></Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{quote.opportunity.name}</Typography>
                        <Typography variant="body2" color="text.secondary">Click to view opportunity</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* System Info Widget */}
              <Card variant="outlined" sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600} sx={{ letterSpacing: 1 }}>SYSTEM INFO</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="caption" fontWeight={500}>
                        {quote.created_at ? format(new Date(quote.created_at), 'MMM d, yyyy h:mm a') : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                      <Typography variant="caption" fontWeight={500}>
                        {quote.updated_at ? format(new Date(quote.updated_at), 'MMM d, yyyy h:mm a') : '-'}
                      </Typography>
                    </Box>
                    {quote.created_by_name && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Created By</Typography>
                        <Typography variant="caption" fontWeight={500}>{quote.created_by_name}</Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {quote.status === QuoteStatus.DRAFT && (
          <MenuItem onClick={() => handleStatusChange(QuoteStatus.SENT)}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mark as Sent</ListItemText>
          </MenuItem>
        )}
        {quote.status === QuoteStatus.APPROVED && (
          <MenuItem onClick={() => handleStatusChange(QuoteStatus.SENT)}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mark as Sent</ListItemText>
          </MenuItem>
        )}
        {canApproveQuote(quote.status) && canEdit && (
          <>
            <MenuItem onClick={() => { setMenuAnchor(null); setApprovalDialogOpen(true); }}>
              <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>Approve Quote</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setMenuAnchor(null); setRejectionDialogOpen(true); }}>
              <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Reject Quote</ListItemText>
            </MenuItem>
          </>
        )}
        {quote.status === QuoteStatus.SENT && (
          <>
            <MenuItem onClick={() => handleStatusChange(QuoteStatus.ACCEPTED)}>
              <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>Mark as Accepted</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleStatusChange(QuoteStatus.REJECTED)}>
              <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Mark as Rejected</ListItemText>
            </MenuItem>
          </>
        )}
        {quote.status === QuoteStatus.ACCEPTED && quote.opportunity_id && (
          <MenuItem onClick={handleSyncToOpportunity}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sync to Opportunity</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleGeneratePdf}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => !approvalLoading && setApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Quote</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are about to approve this quote. Once approved, it can be sent to the customer.
          </Typography>
          <TextField
            fullWidth
            label="Approval Notes (optional)"
            multiline
            rows={3}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add any notes about this approval..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setApprovalDialogOpen(false)}
            disabled={approvalLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveQuote}
            disabled={approvalLoading}
            startIcon={approvalLoading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Approve Quote
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => !approvalLoading && setRejectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Quote</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this quote. The quote will need to be revised before it can be approved.
          </Typography>
          <TextField
            fullWidth
            required
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            error={rejectionReason.trim() === '' && rejectionDialogOpen}
            helperText={rejectionReason.trim() === '' ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectionDialogOpen(false)}
            disabled={approvalLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectQuote}
            disabled={approvalLoading || !rejectionReason.trim()}
            startIcon={approvalLoading ? <CircularProgress size={20} /> : <CloseIcon />}
          >
            Reject Quote
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteDetail;